import { Head, Link } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { RewardForm } from "@/components/rewards/reward-form"
import type { RewardFormReward } from "@/components/rewards/reward-form"
import { Button } from "@/components/ui/button"
import AppLayout from "@/layouts/app-layout"
import { rewardsPath } from "@/routes"

/**
 * OKS-59 — Standalone New Reward page.
 *
 * The primary New UX is the Sheet hosted on `Brand/Rewards/Index`
 * (driven by `?new=1`). This page exists as a direct-route entry
 * point — sidebar deep-link, SSR-first paint, a11y tabbing from
 * outside Inertia. Reuses `<RewardForm>` so the input surface stays
 * identical across both paths.
 */
interface RewardsNewProps {
  reward: RewardFormReward
  kinds: string[]
}

export default function RewardsNew({ reward, kinds }: RewardsNewProps) {
  const { t } = useTranslation()

  return (
    <AppLayout>
      <Head title={t("rewards.form.new_title")} />
      <div className="mx-auto flex max-w-2xl flex-col gap-6 p-6">
        <header className="space-y-1">
          <h1 className="text-foreground text-2xl font-semibold tracking-tight">
            {t("rewards.form.new_title")}
          </h1>
          <p className="text-muted-foreground text-sm">
            {t("rewards.form.description")}
          </p>
        </header>

        <RewardForm
          reward={reward}
          kinds={kinds}
          action={rewardsPath()}
          method="post"
        />

        <div className="flex justify-end">
          <Button asChild variant="ghost">
            <Link href={rewardsPath()}>{t("rewards.form.cancel")}</Link>
          </Button>
        </div>
      </div>
    </AppLayout>
  )
}
